import { createContext } from "preact";
import { useContext, useMemo, useState, useEffect, useCallback } from "preact/hooks";
import { fetchToServer } from "@shared/components";
import { useProjectDetailPage } from "../store";
import toast from "react-hot-toast";

const Ctx = createContext(null);

function getInitialDeliveryDate() {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Provider de pagos
 */
export function PaymentsProvider({ children }) {
  const {
    role,
    projectId,
    fetchProject,
    pendingPaymentIdForDelivery,
    setPendingPaymentIdForDelivery,
  } = useProjectDetailPage();

  // Datos
  const [payments, setPayments] = useState([]);
  const [saving, setSaving] = useState(false);

  // UI state
  const [payOpen, setPayOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [deliveryWhen, setDeliveryWhen] = useState(getInitialDeliveryDate());
  const [defaultMethod, setDefaultMethod] = useState("CARD");
  const [spei, setSpei] = useState({ reference: null, eligibility: null });

  // Admin: Registrar pago manual
  const [registerOpen, setRegisterOpen] = useState(false);

  // Cargar pagos
  const loadPayments = useCallback(async () => {
    const res = await fetchToServer({
      url: `/${role}/api/projects/${projectId}/payments`,
      method: "GET",
    });
    setPayments(res?.data || []);
  }, [projectId, role]);

  // Cargar método por defecto SPEI/CARD
  const loadDefaultMethod = useCallback(async () => {
    try {
      const res = await fetchToServer({ url: `/${role}/api/payments/default-method`, method: "GET" });
      setDefaultMethod(res?.defaultMethod || "CARD");
      setSpei((prev) => ({ ...prev, eligibility: res?.eligibility || null }));
    } catch {}
  }, [role]);

  useEffect(() => {
    loadPayments();
    loadDefaultMethod();
  }, [loadPayments]);

  // Efecto para abrir el modal de entrega cuando el padre lo indique
  useEffect(() => {
    if (pendingPaymentIdForDelivery) {
      const paymentToOpen = payments.find(p => p._id === pendingPaymentIdForDelivery);
      if (paymentToOpen) {
        setSelectedPayment(paymentToOpen);
        setDeliveryOpen(true);
        setPendingPaymentIdForDelivery(null);
      }
    }
  }, [pendingPaymentIdForDelivery, payments, setPendingPaymentIdForDelivery]);


  // Acciones de UI
  const openPay = useCallback((payment) => {
    setSelectedPayment(payment);
    setPayOpen(true);
  }, []);
  const closePay = useCallback(() => setPayOpen(false), []);

  const onPaid = useCallback(() => {
    setPayOpen(false);
    if (selectedPayment) {
      setPendingPaymentIdForDelivery(selectedPayment._id);
    }
  }, [selectedPayment, setPendingPaymentIdForDelivery]);

  const closeDelivery = useCallback(() => setDeliveryOpen(false), []);

  const saveDeliveryAndActivate = useCallback(async () => {
    if (!selectedPayment) return;
    try {
      setSaving(true);
      await fetchToServer({
        url: `/${role}/api/projects/${projectId}/payments/${selectedPayment._id}/delivery`,
        method: "POST",
        data: { deliveryAt: deliveryWhen, note: "" },
      });
      await fetchToServer({
        url: `/${role}/api/projects/${projectId}/status`,
        method: "PUT",
        data: { status: "in-develop" },
      });
      setDeliveryOpen(false);
      setSelectedPayment(null);
      await fetchProject();
      await loadPayments();
    } finally {
      setSaving(false);
    }
  }, [selectedPayment, deliveryWhen, role, projectId, fetchProject, loadPayments]);

  // Admin: Registrar pago
  const openRegister = useCallback(() => setRegisterOpen(true), []);
  const closeRegister = useCallback(() => setRegisterOpen(false), []);
  const registerPayment = useCallback(async ({ paymentId, notes }) => {
    if (role !== 'adevs-admin') return;
    try {
      setSaving(true);
      await fetchToServer({
        url: `/${role}/api/projects/${projectId}/payments/${paymentId}/register`,
        method: "POST",
        data: { notes },
      });
      toast.success("Pago registrado correctamente.");
      closeRegister();
      await fetchProject();
      await loadPayments();
    } catch (err) {
      toast.error(err.message || "No se pudo registrar el pago.");
    } finally {
      setSaving(false);
    }
  }, [role, projectId, fetchProject, loadPayments]);


  const value = useMemo(
    () => ({
      payments,
      saving,
      role,
      projectId,
      // Cliente
      payOpen,
      openPay,
      closePay,
      onPaid,
      deliveryOpen,
      closeDelivery,
      deliveryWhen,
      setDeliveryWhen,
      saveDeliveryAndActivate,
      selectedPayment,
      defaultMethod,
      setDefaultMethod,
      spei,
      setSpei,
      // Admin
      registerOpen,
      openRegister,
      closeRegister,
      registerPayment,
    }),
    [
      payments, saving, role, projectId,
      payOpen, openPay, closePay, onPaid,
      deliveryOpen, closeDelivery, deliveryWhen,
      saveDeliveryAndActivate, selectedPayment,
      defaultMethod, spei,
      registerOpen, openRegister, closeRegister, registerPayment
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePaymentsStore = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePaymentsStore must be used within PaymentsProvider");
  return v;
};
